/* eslint-disable arrow-body-style */
import * as React from "react";
import {autorun, reaction} from "mobx";
import {observer} from "mobx-react";
import {Message} from "semantic-ui-react";

import "./parcel-wizard.scss";

import EsriGraphic from "@arcgis/core/Graphic";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriSketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";

import {Auftragsverwaltung, PerimeterType} from "../../../../../stores/auftragsverwaltung-mobx";

import {MapState} from "../../../../../core/map-state";
import {UiState} from "../../../../../core/ui-state";

import {MapViewerConfiguration} from "../../../../../config/map-viewer-configuration";

import {Project} from "../../../../../models/project";

import {ParcelList} from "./parcel-list/parcel-list";
import {AddButton, ClearButton, SaveButton, UpdateButton} from "./parcel-wizard-tools/parcel-wizard-tools";
import {Parcel} from "../../../../../models/parcel";
import {AppState} from "../../../app-mobx";
import {ParcelStore} from "../../../../../stores/parcel-store-mobx";
import {PerimeterTypeSelector} from "../perimeter-type-selector/perimeter-type-selector";

export enum ParcelWizardMode {
    ParcelSelector = "parcelSelector",
    PerimeterSelector = "perimeterSelector"
}

export interface ParcelWizardProps {
    auftragsverwaltung: Auftragsverwaltung;
    mapViewerConfig: MapViewerConfiguration;
    project: Project | null;
    mapStore: MapState;
    appStore: AppState;
    uiStore: UiState;
}

/**
 * Wizard to create polygon features based on parcels
 */
@observer
export class ParcelWizard extends React.Component<ParcelWizardProps, any> {
    sketchWatchHandle: any;
    autorunDisposers: any[] = [];

    parcelStore: ParcelStore;
    sketchLayer: EsriGraphicsLayer;
    proposalLayer: EsriGraphicsLayer;
    highlightLayer: EsriGraphicsLayer;
    sketchViewModel: EsriSketchViewModel;

    constructor(props: ParcelWizardProps) {
        super(props);
        this.state = {
            mode: null,  // ParcelWizardMode
            updatePerimeter: null,  // the perimeter selected to update
            perimeterProposal: null,  // the proposal generated from all selected parcels
            noParcelsFoundError: false,
            selectedPerimeterType: null  // selected perimeter type
        };
        this.sketchLayer = new EsriGraphicsLayer(
            {
                listMode: "hide",
                title: "SketchLayer ParcelWizard",
                opacity: 0.7
            }
        );
        this.proposalLayer = new EsriGraphicsLayer(
            {
                listMode: "hide",
                title: "ProposalLayer ParcelWizard",
                opacity: 0.7
            }
        );
        this.highlightLayer = new EsriGraphicsLayer(
            {
                listMode: "hide",
                title: "HighlightLayer ParcelWizard"
            }
        );

        this.initStore();
        this.handlePerimeterTypeSelection = this.handlePerimeterTypeSelection.bind(this);
    }

    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((r: any) => {
            const {auftragsverwaltung, mapStore} = this.props;

            if (!auftragsverwaltung.selectedProject || !auftragsverwaltung.apiLoaded) {
                return;
            }

            if (!mapStore.map || !mapStore.view) {
                return;
            }
            r.dispose();

            // update the graphic on map showing the unioned geometry of all selected parcels
            // whenever a parcel is added to or removed from the list
            this.autorunDisposers.push(reaction(
                () => [...this.parcelStore?.items.values()],
                (parcels) => {
                    this.updateParcelPerimeterProposal(parcels);
                }
            ));

            // add proposal layer to the map
            if (this.proposalLayer && !mapStore?.map.layers.includes(this.proposalLayer)) {
                mapStore?.map.add(this.proposalLayer);
            }
            // add sketch layer to the map
            if (this.sketchLayer && !mapStore?.map.layers.includes(this.sketchLayer)) {
                mapStore?.map.add(this.sketchLayer);
            }
            // add highlight layer
            if (this.highlightLayer && !mapStore?.map.layers.includes(this.highlightLayer)) {
                mapStore?.map.add(this.highlightLayer);
            }

            this.clearHighlights();

            this.initSketchViewModel();

            // TODO: workaround. Direktes aufrufen von wirft das erste mal einen Fehler in sketchViewModel.create
            setTimeout(() => {
                this.activateParcelSelector();
            }, 200);
        }));
    }

    componentDidUpdate() {}


    componentWillUnmount(): void {
        this.clearHighlights();
        this.deactivateParcelSelector();

        this.autorunDisposers.forEach((dispose: any) => {
            dispose();
        });
        this.autorunDisposers.length = 0;

        this.sketchViewModel.destroy();
    }

    render() {
        const {auftragsverwaltung, project} = this.props;
        const {mode, noParcelsFoundError} = this.state;
        const hasParcelsSelected = this.parcelStore?.items.size > 0;

        return (
            <div className="parcel-wizard">
                <PerimeterTypeSelector project={project} onSelect={this.handlePerimeterTypeSelection} />
                {!hasParcelsSelected &&
                    <div className="parcel-wizard-perimeter-selector">
                        <Message
                            attached info size={"mini"}
                            header="A) Bestehenden Perimeter auswählen"
                            list={[
                                "In Karte auf Perimeter klicken"
                            ]}
                        />
                        <div className="parcel-wizard-tools">
                            <UpdateButton
                                active={mode === ParcelWizardMode.PerimeterSelector}
                                onUpdate={() => {
                                    this.activatePerimeterSelector();
                                }}
                            />
                        </div>
                    </div>
                }
                <div className="parcel-wizard-parcel-selector">
                    <Message
                        attached info size={"mini"}
                        header="B) Parzellen auswählen"
                        list={[
                            `Mit + Parzellen aus Karte ${hasParcelsSelected ? "hinzufügen" : "auswählen"} (Rechteck aufziehen)`,
                            "Parzellen in Liste prüfen / entfernen"
                        ]}
                    />
                    <div className="parcel-wizard-tools">
                        <AddButton
                            active={mode === ParcelWizardMode.ParcelSelector}
                            onAdd={() => {
                                this.activateParcelSelector();
                            }}
                        />
                        {hasParcelsSelected &&
                            <>
                                <ClearButton onClear={() => {
                                    this.clearSelectedParcels();
                                }} />
                                <SaveButton onSave={() => {
                                    this.saveProjectPerimeter();
                                }} />
                            </>
                        }
                    </div>
                    <div className="parcel-list">
                        <ParcelList
                            auftragsverwaltung={auftragsverwaltung}
                            parcelStore={this.parcelStore}
                            onHover={(e, parcel) => {
                                this.highlightParcelFeature(parcel?.feature);
                            }}
                        />
                        {noParcelsFoundError &&
                            <Message
                                warning size={"mini"}
                                header="Nicht zusammengesetzt aus Parzellen"
                                content="Das gewählte Feature besteht nicht aus Parzellen.
                                 Es kann mit diesem Werkzeug nicht bearbeitet werden."
                            />
                        }
                    </div>
                </div>
            </div>
        );
    }

    private handlePerimeterTypeSelection(type: PerimeterType): void {
        this.setState({selectedPerimeterType: type});
        // update proposal when perimeter type is changed
        this.updateParcelPerimeterProposal([...this.parcelStore?.items.values()], type);
    }

    private updateParcelPerimeterProposal(parcels: Parcel[], type?: PerimeterType): void {
        const {project, auftragsverwaltung, uiStore, appStore} = this.props;
        const {updatePerimeter, selectedPerimeterType} = this.state;

        uiStore?.setPopupVisible(false);
        this.clearProposal(); // clear previously added proposals

        if (parcels.length === 0 || !project?.id) {
            return;
        }

        // merge the geometries of the parcels and add it as sketch proposal
        const geometries = parcels.map((p) => p.feature?.geometry);
        const selectedParcelGeometry = geometryEngine.union(geometries);

        const store = auftragsverwaltung.ProjectPerimeterStore;
        if (!store) {
            throw new Error("Perimeter store undefined when creating feature");
        }

        if (updatePerimeter) {
            // user selected an existing perimeter
            // use the new geometry to update the selected project perimeter
            updatePerimeter.feature.geometry = selectedParcelGeometry;
            this.setState({
                perimeterProposal: updatePerimeter
            });
            this.showProposalOnMap(selectedParcelGeometry);
            // remember in store
            store.proposal = updatePerimeter;
        }
        else {
            // create perimeter from template with the new geometry
            const user = appStore.esriCredential?.userId || "";
            store.newItem(project.id, user, selectedParcelGeometry)
                .then((perimeter) => {
                    if (perimeter) {
                        // set type base on project type
                        // perimeter type selector returns type based on project type and selection
                        perimeter.objectType = type || selectedPerimeterType;

                        this.setState({
                            perimeterProposal: perimeter
                        });
                        this.showProposalOnMap(selectedParcelGeometry);
                        // remember in store
                        store.proposal = perimeter;
                    }
                });
        }
    }

    /**
     * Show graphic on map
     * @param geometry
     * @private
     */
    private showProposalOnMap(geometry: __esri.Geometry): void {
        const {mapViewerConfig} = this.props;

        if (!geometry) {
            throw new Error("Error placing proposal on map. Geometry undefined.");
        }

        const symbol = mapViewerConfig.symbols["perimeterProposal"] as __esri.Symbol;
        const tempGraph = new EsriGraphic({
            geometry,
            symbol,
            layer: this.proposalLayer
        });
        this.proposalLayer.graphics.add(tempGraph);
    }

    /**
     * Select parcels from parcel layer with specified geometry
     * @param geometry
     * @param spatialRelationship
     * @private
     */
    private selectParcels(
        geometry: __esri.Geometry,
        spatialRelationship?: "intersects"
        | "contains"
        | "crosses"
        | "disjoint"
        | "envelope-intersects"
        | "index-intersects"
        | "overlaps"
        | "touches"
        | "within"
        | "relation") {

        this.parcelStore?.queryFeatures(undefined, geometry, spatialRelationship)
            .then((parcels) => {
                if (parcels && parcels.length > 0) {
                    // remember selected parcels
                    this.parcelStore?.addParcels(parcels);
                    // geometry in map and editor is updated by autorun when parcels in parcel store are updated
                }
                else {
                    // geometry is not composed of parcels
                    this.setState({
                        noParcelsFoundError: true
                    });
                }
                // clear drawn polygon from map
                this.clearSelectionSketch();
            })
            .catch((error) => {
                console.error(error);
            });
    }

    /**
     * Select perimeter by point
     * @param geometry
     * @private
     */
    private selectPerimeter(geometry: __esri.Geometry): void {
        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.ProjectPerimeterStore?.queryFeatures(
            undefined,
            undefined,
            geometry)
            .then((perimeters) => {
                if (perimeters && perimeters.length > 0) {
                    const perimeter = perimeters[0];
                    this.setState({
                        updatePerimeter: perimeter
                    });
                    this.selectParcels(perimeter.feature?.geometry, "contains");
                }
                // clear drawn polygon from map
                this.clearSelectionSketch();
            })
            .catch((error) => {
                console.error(error);
            });
    }

    /**
     * Save the proposal as perimeter
     * @private
     */
    private saveProjectPerimeter(): void {
        const {auftragsverwaltung, appStore} = this.props;
        const {updatePerimeter, perimeterProposal} = this.state;

        if (!perimeterProposal) {
            return;
        }

        if (updatePerimeter) {
            // ask if existing perimeter should be updated
            if (!confirm("Bestehenden Perimeter ändern?")) {
                return;
            }
        }

        const user = appStore.esriCredential?.userId || "";
        auftragsverwaltung.ProjectPerimeterStore?.save(perimeterProposal, undefined, user).then(() => {
            this.clearSelectedParcels();
            this.setState({
                updatePerimeter: null,
                perimeterProposal: null
            });
            auftragsverwaltung.ProjectPerimeterStore?.clearProposal();
        });
    }

    /**
     * Clear selection layers
     * @private
     */
    private clearSelectionSketch(): void {
        this.sketchLayer?.removeAll();
    }
    /**
     * Clear proposal layers
     * @private
     */
    private clearProposal(): void {
        const {auftragsverwaltung} = this.props;
        this.proposalLayer?.removeAll();
        auftragsverwaltung.ProjectPerimeterStore?.clearProposal();
    }
    /**
     * Clear highlight layers
     * @private
     */
    private clearHighlights(): void {
        this.highlightLayer?.removeAll();
    }

    private clearSelectedParcels(): void {
        this.parcelStore?.clearItems();
        this.clearHighlights();
        this.clearProposal();
    }

    /**
     * Highlight parcel features in the map
     * @param feature
     * @private
     */
    private highlightParcelFeature(feature: __esri.Graphic): void {
        const {mapViewerConfig} = this.props;

        this.clearHighlights();

        const symbolConfig = mapViewerConfig.symbols["parcelHighlightSymbol"];
        const graphic = new EsriGraphic({geometry: feature.geometry, symbol: symbolConfig});
        this.highlightLayer.add(graphic);
    }

    /**
     * Activate tool to select parcels by dragging rectangle to the map
     * @private
     */
    private activateParcelSelector(): void {
        if (!this.sketchViewModel) {
            throw new Error("ParcelWizard: SketchViewModel not initialized.");
        }
        this.sketchWatchHandle?.remove();

        this.setState({
            mode: ParcelWizardMode.ParcelSelector,
            updatePerimeter: null
        });

        this.sketchWatchHandle =
            this.sketchViewModel.on("create", async (event) => {
                if (event.state === "complete") {
                    this.selectParcels(event.graphic?.geometry);
                }
            });


        // activate tool to draw rectangle
        this.sketchViewModel.create("rectangle");
    }

    /**
     * Activate tool to select existing perimeter and parse as parcel list
     * @private
     */
    private activatePerimeterSelector(): void {
        if (!this.sketchViewModel) {
            throw new Error("ParcelWizard: SketchViewModel not initialized.");
        }
        this.sketchWatchHandle?.remove();

        this.setState({
            mode: ParcelWizardMode.PerimeterSelector,
            noParcelsFoundError: false
        });

        this.sketchWatchHandle =
            this.sketchViewModel.on("create", async (event) => {
                if (event.state === "complete") {
                    this.selectPerimeter(event.graphic?.geometry);
                }
            });


        // activate tool to draw point
        this.sketchViewModel.create("point");
    }

    private deactivateParcelSelector(): void {
        this.setState({
            updatePerimeter: null,
            noParcelsFoundError: false
        });
        this.clearSelectionSketch();
        this.sketchViewModel.cancel();
        this.sketchWatchHandle?.remove();
        this.clearProposal();
    }

    private initSketchViewModel(): void {
        const {auftragsverwaltung, mapStore, mapViewerConfig} = this.props;

        // initialize model to draw a selection box
        this.sketchViewModel = new EsriSketchViewModel({
            view: mapStore?.view,
            layer: this.sketchLayer,
            tooltipOptions: {
                enabled: true
            },
            polygonSymbol: mapViewerConfig.symbols["parcelSelection"] as __esri.SimpleFillSymbol
        });
        // watch changes in application edit mode
        this.autorunDisposers.push(autorun((editingReaction: any) => {
            if(!auftragsverwaltung.isEditingProject) {
                editingReaction.dispose();
                // deactivate parcel selector when editing mode is disabled
                this.deactivateParcelSelector();
            }
        }));
    }

    private initStore(): void {
        const {mapStore, mapViewerConfig} = this.props;
        const layerConfig = mapViewerConfig.auftragsverwaltungApi.getLayerConfig("parcel");
        if (!layerConfig) {
            throw new Error("Configuration not found for parcel layer.");
        }
        this.parcelStore = new ParcelStore({
            layerConfig,
            mapStore
        });
        this.parcelStore.loadApi();
    }
}
